import React from 'react'
import Link, { withPrefix } from 'gatsby-link'

//  Components
import caseStudyImageUrl, { ReactComponent as CaseStudyImage } from '../../assets/svg/case-study-two-states.svg'
import caseStudyNewYorkImageUrl, { ReactComponent as CaseStudyNewYorkImage } from '../../assets/svg/case-study-new-york.svg'
import caseStudyTwoStatesImageUrl, { ReactComponent as CaseStudyTwoStatesImage } from '../../assets/svg/case-study-two-states.svg'
import caseStudyMortalityImageUrl, { ReactComponent as CaseStudyMortalityImage } from '../../assets/svg/case-study-hospital-mortality.svg'
import Button from '../../components/Button/Button.js'

const MedicaidExpansion = ({ data }) => (
  <div>
    <article className="case_study">
      <div className="section">
        <div className="section__row section__row__2_up section__row__reverse">
          <figure className="section__content section__content__figure case_study__figure">
            <div className="case_study__figure__wrapper">
              <CaseStudyImage />
            </div>
          </figure>
          <div className="section__content section__content__limit">
            <h2 className="title__page__label">A Closer Look:</h2>
            <h1 className="title__page">New Hampshire and Maine:<br />
            <em>Two States Where Medicaid Expansion Is in Flux</em></h1>
            <p>Before the Affordable Care Act expanded coverage, New Hampshire greatly lagged Maine in terms of access to health care for low-income adults. But in August 2014, New Hampshire expanded its Medicaid program and has since caught up to its New England neighbor, matching Maine on its uninsured rate among low-income adults and nearly closing the gap on several other key Scorecard measures of health care access. Maine has not only failed to take advantage of its early lead, it has lost ground on some measures.</p>
            <p>In November 2017, Maine residents voted to expand Medicaid under a citizen-initiated ballot referendum. The expansion is estimated to extend Medicaid coverage to 70,000 to 80,000 Mainers<sup className="note__link"><a href="#45">45</a></sup> with incomes up to 138 percent of the federal poverty level ($16,753 for a single person in 2018), although implementation has been stalled because of funding conditions set by the governor. New Hampshire is facing the expiration of its Medicaid expansion in December 2018, and it’s not clear whether the state legislature will reauthorize it. The result could be a loss of coverage for an estimated 50,000 Granite State adults.<sup className="note__link"><a href="#46">46</a></sup></p>
            <p>The Scorecard offers an inkling of what New Hampshire could lose by dropping Medicaid expansion and what Maine might gain by adding it.</p>
          </div>
        </div>
      </div>

      <hr className="section__divider"/>

      <div className="section">
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h3>Adult uninsured rates drop more in New Hampshire than in Maine</h3>
            <p>Although the low-income adult uninsured rate fell to 20 percent in both states in 2016, in New Hampshire, this represented a 14 percentage point drop from its 2013 rate. Maine saw a 6 percentage point decline in the same time period.</p>
          </div>
          <div className="section__content section__content__wide section__exhibit">
            <h2 className="exhibit__title__block">Income-related disparities in health care access differ across states</h2>
            <figure>
              <img src={ withPrefix(`/img/exhibits/exhibit-box-1-web.png`) } alt="" />
              <figcaption>
                <p>Notes: Low-income is &lt; 200% of the federal poverty level. The difference between the 2013 and 2016 rates in New Hampshire on all three access indicators among low-income adults meet the Scorecard’s threshold for improvement for these indicators, as does the does the difference in Maine in uninsured rates. The increase in low-income adults who went without care because of costs in Maine meets the Scorecard’s threshold for worsening on this measure. See Scorecard Methods and <a href={ withPrefix(`/files/Radley_State_Scorecard_2018_APPENDIX.pdf`)}>Appendix H</a> for more detail.</p>
                <p>Data: Uninsured, U.S. Census Bureau, 2013 and 2016 One-Year American Community Surveys. Public Use Micro Sample (ACS PUMS); Cost barriers, 2013 and 2016 Behavioral Risk Factor Surveillance System (BRFSS); Premature death, 2012–2015 National Vital Statistics System (NVSS) Mortality All-County Micro Data Files.</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>

      <hr className="section__divider"/>

      <div className="section">
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h3>Number of people skipping care because of costs drops in New Hampshire, increases in Maine</h3>
          </div>
          <div className="section__content section__content__wide">
            <p>New Hampshire saw a 9 percentage point reduction in the share of low-income adults age 18 and older who reported not going to the doctor when needed because costs were too high. In Maine, the share of adults with low incomes who said they skipped needed care because they couldn’t afford it increased 4 percentage points between 2013 and 2016.</p>
          </div>
        </div>
      </div>

      <hr className="section__divider"/>

      <div className="section">
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h3>Share of low-income adults without a usual source of care drops in New Hampshire, stays the same in Maine</h3>
          </div>
          <div className="section__content section__content__wide">
            <p>New Hampshire also nearly caught up with Maine on another key measure of health care access: having a regular doctor or health care provider. The share of low-income adults age 18 and older who did not have a usual source of care decreased 6 percentage points in New Hampshire between 2013 and 2016; the rate remained unchanged in Maine.</p>
          </div>
        </div>
      </div>

      <hr className="section__divider"/>

      <div className="section">
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h3>Premature death rates rise in Maine, decrease in New Hampshire</h3>
          </div>
          <div className="section__content section__content__wide">
            <p>In Maine, the rate of premature death from preventable or treatable causes — a measure called “mortality amenable to health care” in the Scorecard — increased from 62.3 to 66.2 per 100,000 people between 2012–2013 and 2014–2015. This represented a 6.3 percent increase — one of the highest in the nation. Only three states saw larger relative increases. New Hampshire, on the other hand, was one of only 14 states that saw a decline in this rate, with a drop from 58.4 to 57.7 per 100,000 people, about a 1.1 percent decline.</p>
          </div>
        </div>
      </div>
    </article>
    <div className="section section__case_study__list">
      <div className="section__row section__row__3_up">
        <div className="section__content">
          <figure className="case_study__figure">
            <div className="case_study__figure__wrapper">
              <CaseStudyTwoStatesImage />
            </div>
          </figure>
          <div className="case_study__content">
            <h4 className="case_study__label">A Closer Look:</h4>
            <h3 className="case_study__title">New Hampshire and Maine: Two States Where Medicaid Expansion Is in Flux</h3>
            <Button to="/case-study/medicaid-expansion-in-flux" text="Keep Reading" />
          </div>
        </div>
        <div className="section__content">
          <figure className="case_study__figure">
            <div className="case_study__figure__wrapper">
              <CaseStudyMortalityImage />
            </div>
          </figure>
          <div className="case_study__content">
            <h4 className="case_study__label">A Closer Look:</h4>
            <h3 className="case_study__title">Hospital Mortality</h3>
            <Button to="/case-study/hospital-mortality" text="Keep Reading" />
          </div>
        </div>
        <div className="section__content">
          <figure className="case_study__figure">
            <div className="case_study__figure__wrapper">
              <CaseStudyNewYorkImage />
            </div>
          </figure>
          <div className="case_study__content">
            <h4 className="case_study__label">A Closer Look:</h4>
            <h3 className="case_study__title">New York Moves to Improve</h3>
            <Button to="/case-study/new-york-moves-to-improve" text="Keep Reading" />
          </div>
        </div>
      </div>
    </div>
    <div className="section section__notes">
      <div className="section__row">
        <div className="section__content">
          <ol start="45" style={{counterReset: "item 44"}} className="list__notes">
            <li id="45">April Grady, Patricia M. Boozang, and Cindy Mann, <em>Estimated Budget Impacts of Expanding MaineCare, Prepared for the Maine Health Access Foundation</em> (Manatt Health, Feb. 28, 2018), <a href="https://www.manatt.com/Insights/White-Papers/2018/Estimated-Budget-Impacts-of-Expanding-MaineCare"><span>https://www.manatt.com/Insights/White-Papers/2018/Estimated-Budget-Impacts-of-Expanding-MaineCare</span></a>; Elizabeth H. Kilbreth, <em>The Real Impact of Medicaid Expansion in Maine</em> (Maine Equal Justice Partners, Sept. 26, 2017), <a href="http://www.mejp.org/sites/default/files/Medicaid-Expansion-The-Real-Impact-Kilbreth-Sep2017.pdf"><span>http://www.mejp.org/sites/default/files/Medicaid-Expansion-The-Real-Impact-Kilbreth-Sep2017.pdf</span></a>; and Yes on 2, Mainers for Health Care, “Get the Facts on Question 2: Medicaid Expansion Will Help Maine,” (see question 14 in document), <a href="http://mejp.org/sites/default/files/Medicaid-Expansion-Get-the-Facts-10-5-17.pdf"><span>http://mejp.org/sites/default/files/Medicaid-Expansion-Get-the-Facts-10-5-17.pdf</span></a>.</li>
            <li id="46">NewHampshireFiscalPolicyInstitute,“Medicaid Expansion in New Hampshire and the State Senate’s Proposed Changes,” Mar. 30, 2018, <a href="http://nhfpi.org/research/health-policy/medicaid-expansion-in-new-hampshire-and-the-state-senates-proposed-changes.html"><span>http://nhfpi.org/research/health-policy/medicaid-expansion-in-new-hampshire-and-the-state-senates-proposed-changes.html</span></a>.</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
)

export default MedicaidExpansion

export const query = graphql`
  query MedicaidQuery {
    site {
      pathPrefix
    }
  }
`



// WEBPACK FOOTER //
// ./src/pages/case-study/medicaid-expansion-in-flux.js